import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GlobalParticipantType } from '@vendasta/conversation';
import { Citation, CitationType, METADATA_KEY_CITATIONS, METADATA_KEY_REASONING, NamespaceType } from './citations';

import { TranslateModule } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GlxyMarkdownPipe } from '@vendasta/galaxy/pipes';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  CONVERSATION_ROUTES_TOKEN,
  ConversationService,
  getAccountGroupIDFromConversation,
  getConversationIdFromDetail,
  KeyValuePair,
} from '../../../../../../../core/src';
import { AiKnowledgeModule, BUSINESS_PROFILE_URL_TOKEN, MANAGE_KNOWLEDGE_URL_TOKEN } from '@galaxy/ai-knowledge';
import { MatDialog } from '@angular/material/dialog';
import { FunctionCallModalComponent, FunctionCallModalData } from './function-call/function-call-modal.component';
import {
  WebsitePreviewModalComponent,
  WebsitePreviewModalData,
} from './website-preview-modal/website-preview-modal.component';
import { Router } from '@angular/router';

@Component({
  selector: 'inbox-citations-component',
  templateUrl: './citations.component.html',
  styleUrls: ['./citations.component.scss'],
  imports: [MatIconModule, MatTooltipModule, TranslateModule, AiKnowledgeModule],
  providers: [GlxyMarkdownPipe],
})
export class CitationsComponent {
  CitationType = CitationType;
  messageMetadata = input<KeyValuePair[]>([]);
  messageContent = input<string>('');
  isReasoningMode = input<boolean>(false);

  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN));
  private readonly markdownPipe = inject(GlxyMarkdownPipe);

  private readonly conversationService = inject(ConversationService);
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly isMultiLocation = this.hostAppInterface.getAppOptions().is_multilocation;
  private readonly viewer = toSignal(this.hostAppInterface.getNamespace());

  isExpanded = signal(false);

  constructor() {
    effect(() => {
      const content = this.messageContent();
      if ((!content || content.trim() === '') && this.isReasoningMode()) {
        this.isExpanded.set(true);
      } else {
        this.isExpanded.set(false);
      }
    });
  }

  toggleCitations() {
    this.isExpanded.update((prev) => !prev);
  }

  // The tokens here assume that knowledge is defined in one place for the App, but this isn't the case for
  // multi-location in Business App, so we need to link to the conversation's account group routes specifically in those case
  private accountGroupIdToken = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private conversationDetails = toSignal(this.conversationService.currentConversationDetail$);
  private accountGroupId = computed(() => {
    if (this.isMultiLocation) {
      return getAccountGroupIDFromConversation(this.conversationDetails()?.conversation);
    }
    return this.accountGroupIdToken() || '';
  });

  private knowledgeURLToken = toSignal(inject(MANAGE_KNOWLEDGE_URL_TOKEN));
  private knowledgeURL = computed(() => {
    if (this.isMultiLocation) {
      return `/account/location/${this.accountGroupId()}/settings/ai-knowledge`;
    }
    return this.knowledgeURLToken() || '';
  });

  private businessProfileURLToken = toSignal(inject(BUSINESS_PROFILE_URL_TOKEN));
  private businessProfileURL = computed(() => {
    if (this.isMultiLocation) {
      return `/account/location/${this.accountGroupId()}/settings/profile`;
    }
    return this.businessProfileURLToken() || '';
  });

  private stripHeadings(markdown: string): string {
    return markdown
      .split('\n')
      .filter((line) => !line.trim().startsWith('#'))
      .join('\n')
      .trim();
  }

  reasoning = computed((): string => {
    if (!this.messageMetadata()) return '';
    for (const m of this.messageMetadata()) {
      if (m.key !== METADATA_KEY_REASONING) continue;

      let rawContent = '';
      try {
        rawContent = JSON.parse(m.value || '""') as string;
      } catch {
        rawContent = m.value || '';
      }

      const withoutHeadings = this.stripHeadings(rawContent);
      return this.markdownPipe.transform(withoutHeadings) as string;
    }
    return '';
  });

  citations = computed(() => {
    if (!this.messageMetadata()) return [];

    for (const m of this.messageMetadata()) {
      if (m.key !== METADATA_KEY_CITATIONS) continue;

      if (m.value && m.value.length > 0) {
        const rawCitations = JSON.parse(m.value) as Citation[];
        // Citations for the same source should be deduped because the specific content cited is not a part of the UI citation
        // However, we should include separate citations for each function call since they show the full arguments and response of each call
        const dedupedCitations: Citation[] = [];
        for (const citation of rawCitations) {
          if (citation.type === CitationType.TYPE_FUNCTION_CALL) {
            dedupedCitations.push(citation);
            continue;
          }

          const isDuplicate = dedupedCitations.find((c) => {
            return c.type === citation.type && c.title === citation.title;
          });

          if (!isDuplicate) {
            // Empty link for a cross-namespace source so it can't deep-link into the wrong workspace.
            citation.link = this.isCitationNavigable(citation)
              ? this.buildCitationLink(citation.type ?? 0, citation.link)
              : '';
            dedupedCitations.push(citation);
          }
        }
        return dedupedCitations;
      }

      break;
    }

    return [];
  });

  // Only these citation types build a namespace-scoped `?editSource=` link that can resolve in the
  // wrong workspace cross-namespace; Website / BusinessProfile / FunctionCall links can't, so aren't gated.
  private static readonly NAMESPACE_SCOPED_TYPES: ReadonlySet<CitationType> = new Set([
    CitationType.TYPE_USER_INPUT,
    CitationType.TYPE_USER_INPUT_V2,
    CitationType.TYPE_FILE,
  ]);

  // The (type, id) the citation's source namespace is compared against. In multi-location Business App
  // the Inbox is rooted in a Group namespace, but knowledge is per-location AccountGroup, so gate against
  // the conversation's account group instead. A non-AccountGroup/Partner viewer otherwise maps to '' →
  // never matches a source namespace → non-navigable, the safe default.
  private citationViewerNamespace(): { type: string; id: string } {
    if (this.isMultiLocation) {
      return { type: NamespaceType.ACCOUNT_GROUP, id: this.accountGroupId() };
    }
    switch (this.viewer()?.namespaceType) {
      case GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP:
        return { type: NamespaceType.ACCOUNT_GROUP, id: this.viewer()?.id ?? '' };
      case GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER:
        return { type: NamespaceType.PARTNER, id: this.viewer()?.id ?? '' };
      default:
        return { type: '', id: '' };
    }
  }

  isCitationNavigable(citation: Citation): boolean {
    if (citation.type === undefined || !CitationsComponent.NAMESPACE_SCOPED_TYPES.has(citation.type)) {
      return true;
    }
    // Legacy citations with no source namespace carry no gating signal — keep existing behaviour.
    if (!citation.sourceNamespaceType || !citation.sourceNamespaceId) {
      return true;
    }
    const viewer = this.citationViewerNamespace();
    return citation.sourceNamespaceType === viewer.type && citation.sourceNamespaceId === viewer.id;
  }

  private buildCitationLink(type: CitationType, id: string): string {
    switch (type) {
      case CitationType.TYPE_INVALID:
        return id;
      case CitationType.TYPE_WEBSITE:
      case CitationType.TYPE_WEBSITE_V2:
        return id;
      case CitationType.TYPE_BUSINESS_PROFILE:
      case CitationType.TYPE_BUSINESS_PROFILE_V2: {
        return this.businessProfileURL();
      }
      case CitationType.TYPE_USER_INPUT:
      case CitationType.TYPE_USER_INPUT_V2: {
        return `${this.knowledgeURL()}?editSource=${id}`;
      }
      case CitationType.TYPE_FILE: {
        return `${this.knowledgeURL()}?editSource=${id}`;
      }
      default:
        return id;
    }
  }

  private conversationId = computed(() => getConversationIdFromDetail(this.conversationDetails()));

  openFunctionCallCitationDetails(c: Citation): void {
    this.dialog.open(FunctionCallModalComponent, {
      width: '950px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: {
        sessionEventId: c.sessionEventId || '',
        conversationId: this.conversationId(),
        namespaceType: c.namespaceType,
        namespaceID: c.namespaceId,
        title: c.title,
        description: c.description,
        inlineContent: c.content,
        inlineArguments: c.functionArguments,
      } as FunctionCallModalData,
      autoFocus: false,
    });
  }

  supportsFilePreview(citation: Citation): boolean {
    return citation.type === CitationType.TYPE_WEBSITE || citation.type === CitationType.TYPE_WEBSITE_V2;
  }

  openWebsitePreview(citation: Citation): void {
    if (!citation.fileUrl || !this.supportsFilePreview(citation)) return;

    this.dialog.open(WebsitePreviewModalComponent, {
      data: {
        fileUrl: citation.fileUrl,
        fileName: citation.title,
        liveUrl: citation.link,
        citationType: citation.type,
      } as WebsitePreviewModalData,
      panelClass: 'mat-dialog-no-padding',
      autoFocus: false,
    });
  }
}
